/**
 * The renderer's presentation constants: geometry proportions of the
 * placeholder unit proxies and floor kit, shading factors, camera framing,
 * animation durations, and (group `medium`) the Medium tier's kit: fixture
 * shapes and placement distances, bake and lens settings, budgets. These are
 * presentation, not balance: no rule of the game reads them, and changing one
 * changes only how a floor looks or how long an animation takes. Colours and
 * the theme's look come from the theme (`@ab/content`), never from here.
 * Values are lifted from the dungeon-floors prototype (the file each group
 * names), in world units where one unit is one tile. The arithmetic inside a
 * lifted shader or procedural generator (luma weights, hash constants, tone
 * steps) stays in the code it was lifted with; its knobs are here.
 */
export const presentation = {
  /** Animation durations in milliseconds (`dungeon-motion.js`: 240 ms per tile). */
  anim: {
    moveMsPerTile: 240,
    lungeMs: 180,
    flashMs: 260,
    fallMs: 280,
    standMs: 220,
    bobMs: 320,
    removeMs: 120,
    /** Slice 2b's gestures (`dungeon-events.js`: hop 320, shake 350, collapse 550, ascend 500, cheer 600). */
    castMs: 320,
    tossMs: 260,
    targetFlashMs: 220,
    statusMs: 200,
    stageMs: 240,
    recoilMs: 220,
    ringMs: 420,
    skipMs: 350,
    popMs: 180,
    burstMs: 480,
    bossFallMs: 900,
    eggHopMs: 700,
    escapeMs: 500,
    /** A frame interval longer than this is clamped, so a background tab does not skip an animation. */
    maxFrameMs: 100,
  },
  /**
   * Gesture sizes in tiles (`dungeon-events.js` `update`): the cast hop (.22),
   * an item toss, a stage chevron's pulse, the recoil of a broken charge, the
   * sway of a skipped turn (.05, 30 radians over the gesture), a waking pop, the
   * charge's held crouch and tremble (.03 at t/30 ms), an escape's rise, the
   * egg's hop arc. Units are moved, never scaled.
   */
  gesture: {
    castLift: 0.22,
    tossLift: 0.12,
    stageLift: 0.06,
    recoil: 0.12,
    sway: 0.05,
    swayWaves: 30,
    popLift: 0.16,
    chargeCrouch: -0.03,
    tremble: 0.03,
    trembleMs: 30,
    escapeRise: 0.35,
    eggArc: 0.6,
    reviveLift: 0.1,
  },
  /** Ring flashes on the ground (a passive, a charm's burst): start and end radius in tiles, height, segments. */
  ring: { from: 0.2, to: 0.75, burstTo: 1.1, inner: 0.82, y: 0.03, segments: 24, capacity: 8 },
  /** How far above a unit's head its DOM label anchor sits, in tiles (the prototype anchored labels at 1.05 over the feet). */
  labelLift: 0.12,
  /** Sides of the cylinders (spring basins, the boss dais). */
  roundSegments: 16,
  /** How far a lunge reaches toward its target, and how high an item bobs. */
  lungeDistance: 0.28,
  bobHeight: 0.14,
  /** Baked vertex shading of a box's faces (the Off tier is unlit, ADR 0006). */
  shade: { top: 1, northSouth: 0.8, eastWest: 0.68, bottom: 0.5 },
  /** Fog of war (`updateSeen` in `dungeon-main.js`): reveal radius in tiles and how unseen colours fade. */
  fog: { radius: 5.5, window: 6, fade: 0.86, darken: 0.63, markerFade: 0.92, markerDarken: 0.4, glowDarken: 0.08 },
  /** Follow camera (`cameraGoal` in `dungeon-main.js`, the follow branch). */
  camera: {
    fov: 32,
    near: 0.25,
    far: 300,
    height: 9,
    distance: 12,
    pull: 0.8,
    targetY: 0.2,
    portraitAspect: 0.8,
    portraitScaleMax: 1.35,
    /** Portrait look-ahead (`followRig.lead` in `dungeon-main.js`): the target leads north by this many tiles at full portrait, where portrait = clamp((1 - aspect) / leadAspectRange, 0, 1). */
    lead: 1.4,
    leadAspectRange: 0.5,
  },
  /** Floor and walls (`dungeon-world.js`). */
  floor: { slabY: -0.12, slabHeight: 0.24, grainSpread: 2 },
  wall: { height: 1.55, cutawayHeight: 0.24, trimHeight: 0.05, trimWidth: 0.09, cornerSize: 0.16 },
  /** Timber frames and rock shoulders (`dungeon-tile-kit.js`); the track kit is `rails`. */
  props: {
    heightJitter: 0.08,
    post: 0.17,
    band: 0.181,
    bandHeight: 0.05,
    /** Height of the lower iron band, and how far below the top the upper one sits. */
    lowerBandY: 0.23,
    upperBandDrop: 0.2,
    beamOverhang: 0.13,
    beamLift: 0.06,
    brace: 0.11,
    braceLength: 0.51,
    braceTilt: 0.74,
    beamHeight: 0.2,
    beamDepth: 0.23,
    corridorFrameLength: 1.08,
    roomFrameInset: 0.72,
    roomFrameOffset: 0.32,
    /** Frame parts starting below this height are structures the set dressing keeps clear of. */
    lowStructure: 0.8,
    rockDensityScale: 0.56,
    rockTilt: 0.55,
    /** Rock shoulder sizes: each is [base, extra × the tile's random draw]. */
    rock: {
      wideOffset: 0.31,
      wideSpan: [1.35, 0.55],
      narrowSpan: [0.74, 0.25],
      width: [0.63, 0.3],
      depth: [0.63, 0.27],
      stubHeight: [0.18, 0.11],
      tallHeight: [0.74, 0.83],
      stubSink: 0.045,
      tallSink: [0.18, 0.17],
    },
  },
  /**
   * The track kit (slice 2b, `dungeon-tile-kit.js`): gauge (half the distance
   * between rails), rail and tie sizes, where a rail starts and ends in its
   * tile, the rail's seat into the ties and its overlap at joints, bend
   * segments and radial ties, turnout ties, the straight lattice's reach
   * toward a non-straight neighbour and its jitter, dead-end ties and stop
   * block, the ballast bed and grime streaks. Routes: at most three, ranked by
   * length times [base + extra × a seeded draw], within a share of the detail budget.
   */
  rails: {
    gauge: 0.24,
    railWidth: 0.052,
    railHeight: 0.064,
    tieHeight: 0.055,
    tieWidth: 0.15,
    tieLength: 0.86,
    edge: 0.505,
    deadStart: 0.03,
    seat: 0.005,
    overlap: 0.012,
    bendSegments: 5,
    bendTies: [0.25, 0.75],
    bendTieRadius: 0.57,
    bendTie: [0.7, 0.15],
    turnout: { offsets: [-1 / 3, 0, 1 / 3], inner: 0.43, outer: 0.3, branch: 0.4, branchWidth: 0.14 },
    latticeReach: 0.38,
    skew: 0.03,
    skewTurn: 0.09,
    lengthJitter: 0.08,
    weather: [0.84, 0.3],
    deadTies: [0.08, 0.38],
    buffer: { lift: 0.068, size: [0.12, 0.14, 0.58] },
    ballast: { y: 0.007, height: 0.012, size: 0.98, dead: 0.5, deadOffset: 0.26, shade: 0.8 },
    grime: { count: 3, shade: 0.68, top: 0.0155, step: 0.0009, deadAlong: [0.14, 0.26], along: 0.6, across: 0.5, turn: 0.5, loose: 0.5, length: [0.26, 0.24], width: [0.07, 0.07] },
    maxRoutes: 3,
    routeRank: [0.8, 0.4],
    budgetShare: 0.68,
  },
  /**
   * Set dressing (slice 2b, `dungeon-dressing.js`, THEME-KITS "Dressing"):
   * the triangles each batch's geometry costs per instance, the tier caps in
   * triangles per floor (tier 0 at Off and Medium, tiers 1 and 2 at Medium),
   * the gap to the wall, clearances (a piece's inner shrink, markers and
   * units, structures, other dressing, its own cluster, the swept route
   * radius), feature radii, torch-site reservations, ledge and scatter
   * limits, cart candidates, the border apron, and the density the filler and
   * scatter chances are scaled against.
   */
  dressing: {
    triangles: { timber: 12, iron: 12, rock: 20, timberRound: 44, ironRound: 28, foliage: 28 },
    tierTriangles: [6800, 14000, 12000],
    wallGap: 0.012,
    clearance: { inner: 0.004, feature: 0.08, structure: 0.06, dressing: 0.05, cluster: 0.015, route: 0.23 },
    feature: { unit: 0.55, boss: 0.95, item: 0.55, spring: 0.85, exit: 1.05 },
    torchSite: { half: 0.22, site: [0.4, 0.36], corner: 0.38, wideRoom: 6 },
    segmentSamples: 8,
    nearSite: 2.2,
    nearPost: 1.6,
    ledgeWallMax: 0.6,
    ledgeInset: 0.1,
    ledgeMaxHeight: 0.5,
    stubMaxHeight: 0.4,
    foregroundMaxHeight: 0.6,
    scatterMaxHeight: 0.14,
    scatterCentre: 0.28,
    scatterJitter: 0.16,
    scatterReach: 0.9,
    scrapRail: 0.7,
    fillerCompanion: 0.7,
    cartCandidates: 14,
    referenceDensity: 0.72,
    ledgeSlope: 0.1,
    plateauSlope: 0.2,
    apron: { salt: 977, step: 1.4, max: 12, jitter: 0.8, near: 0.95, far: 0.45, back: 0.15, top: 0.24 },
  },
  /** Markers (`dungeon-markers.js`): sizes are [x, y, z] in tiles. */
  marker: {
    exit: {
      sill: { offset: 0.445, y: -0.14, size: [0.11, 0.28, 1] },
      bottom: { drop: 0.07, size: [0.78, 0.1, 1] },
      rear: { z: 0.47, size: [0.78, 0, 0.06] },
      step: { run: 0.95, width: 0.78, height: 0.052, top: -0.026, shadeStart: 0.83, shadeRange: 0.6 },
      post: { offset: 0.44, z: 0.38, size: [0.14, 1.1, 0.18] },
      band: { heights: [0.16, 0.83], size: [0.154, 0.075, 0.19] },
      lintel: { y: 1.1, size: [1.06, 0.18, 0.21] },
    },
    spring: {
      base: { y: 0.026, size: [0.47, 0.05, 0.47] },
      rim: { stones: 12, radius: 0.38, height: [0.085, 0.07], width: [0.15, 0.09], depth: 0.14, lift: 0.035 },
      water: { y: 0.094, size: [0.325, 0.019, 0.325] },
    },
    dais: {
      base: { y: 0.041, size: [0.54, 0.082, 0.54] },
      top: { y: 0.106, size: [0.5, 0.048, 0.5] },
      rune: { count: 8, radius: 0.395, y: 0.174, size: [0.065, 0.008, 0.028] },
      elevation: 0.17,
    },
    /** How much darker a marker's recessed or foundation parts are than their palette colour. */
    darkShade: 0.55,
  },
  /**
   * Marker detail (slice 2b, `dungeon-markers.js`): the exit's recesses, tread
   * nosings, portal grain and scars and its hanging lantern; the spring's
   * mineral bands, chips and spout; the boss room's dais layers, chunks and
   * runes, corner braziers, back-wall frame with its seal, and inlaid plates.
   * Sizes are [x, y, z] in tiles; `shade` and `mix` values turn theme colours
   * into the prototype's fixed accent shades.
   */
  markerDetail: {
    exit: {
      recess: { offset: 0.4, width: 0.04, wallMix: 0.3 },
      nosing: { lift: 0.03, forward: 0.475, back: 0.023, size: [0.77, 0.008, 0.046], wallMix: 0.15 },
      grain: { offset: 0.472, y: 0.53, z: 0.277, size: [0.023, 0.67, 0.012], shade: 1.4 },
      edge: { y: 1.02, z: 0.265, size: [0.93, 0.018, 0.016], shade: 1.6 },
      lantern: { x: 0.44, y: 0.73, z: 0.24, height: 0.3, frameDrop: 0.02, frame: [0.16, 0.08, 0.13], glass: [0.105, 0.012], glassForward: 0.069, mullion: [0.024, 0.02, 0.014], mullionForward: 0.078 },
      scars: { count: 8, offset: 0.421, jitter: 0.028, y: [0.23, 0.65], z: 0.282, size: [0.014, 0.01], height: [0.08, 0.16], dark: 0.65, light: 1.45 },
    },
    spring: {
      radialJitter: 0.027,
      shade: [0.72, 0.4],
      bandChance: 0.34,
      bandOffset: 0.22,
      bandTurn: 0.29,
      bandLift: 0.039,
      bandWidth: [0.24, 0.38],
      bandHeight: 0.014,
      bandDepth: [0.065, 0.035],
      bandMix: [0.3, 0.1],
      chipOut: 0.036,
      chipY: 0.055,
      chipWidth: 0.5,
      chipHeight: 0.028,
      chipDepth: 0.18,
      chipShade: 0.75,
      chipTurn: 0.13,
      spout: { y: 0.15, z: 0.37, size: [0.16, 0.22, 0.17], lipY: 0.225, lipZ: 0.335, lip: [0.12, 0.045, 0.18], lipMix: 0.25, flowY: 0.167, flowZ: 0.29, flow: [0.058, 0.095, 0.022], flowMix: 0.2, sourceY: 0.202, sourceZ: 0.28, source: [0.065, 0.02, 0.025] },
      /** The water's ripple (Medium): rings per unit radius, speed, sharpness, and the centre and ring brightness. */
      ripple: { rings: 48, speed: 2, sharpness: 12, base: 0.72, centre: 0.18, ring: 0.35, centreEdge: [0.18, 0.49] },
    },
    boss: {
      cornerInset: 0.24,
      darkMix: 0.15,
      midMix: 0.2,
      midShade: 0.8,
      daisTop: { y: 0.146, size: [0.455, 0.04, 0.455], shade: 0.8 },
      chunks: 12,
      chunkRadius: 0.485,
      chunkJitter: 0.023,
      chunkY: 0.091,
      chunk: [0.19, 0.08, 0.105],
      chunkExtra: 0.036,
      chipRadius: 0.492,
      chipY: 0.139,
      chip: [0.085, 0.014, 0.06],
      chipShade: 1.3,
      runeY: 0.174,
      notchRadius: 0.434,
      notch: [0.021, 0.008, 0.045],
      notchMix: 0.5,
      brazier: {
        footY: 0.044,
        foot: [0.18, 0.088, 0.18],
        legX: 0.08,
        legY: 0.28,
        leg: [0.055, 0.47, 0.07],
        legTilt: 0.21,
        grainX: 0.075,
        grainY: 0.38,
        grainZ: 0.041,
        grain: [0.024, 0.16, 0.013],
        grainShade: 1.4,
        bowlY: 0.5,
        bowl: [0.19, 0.085, 0.19],
        wellY: 0.551,
        well: [0.142, 0.028, 0.142],
        wellShade: 1.2,
        prongRadius: 0.15,
        prongY: 0.59,
        prong: [0.045, 0.19, 0.045],
        coalRadius: 0.06,
        coalY: 0.585,
        coalJitter: 0.025,
        coal: [0.065, 0.055, 0.065],
        coalMix: 0.5,
        collarY: 0.38,
        collar: [0.22, 0.05, 0.115],
      },
      frame: {
        inset: 0.25,
        minSpan: 1.1,
        back: 0.28,
        postY: 0.65,
        post: [0.16, 1.3, 0.2],
        strapHeights: [0.13, 1.15],
        strap: [0.18, 0.055, 0.22],
        grainStep: 0.038,
        grainY: 0.6,
        grainRise: 0.12,
        grainForward: 0.106,
        grain: [0.01, 0.48, 0.008],
        grainLight: 1.5,
        grainDark: 0.65,
        top: 1.37,
        beamOverhang: 0.32,
        beamHeight: 0.18,
        beamDepth: 0.23,
        edgeDrop: 0.066,
        edgeForward: 0.12,
        edgeOverhang: 0.25,
        edgeSize: [0.014, 0.01],
        braceInset: 0.21,
        braceY: 1.13,
        braceForward: 0.03,
        brace: [0.11, 0.51, 0.13],
        braceShade: 1.2,
        braceTilt: 0.73,
        sealMargin: 0.22,
        sealY: 1.24,
        sealForward: 0.14,
        seal: [0.34, 0.25, 0.05],
        cutLift: 0.01,
        cutForward: 0.175,
        cut: [0.034, 0.14, 0.008],
        notchOffset: 0.067,
        notch: [0.024, 0.08, 0.008],
        notchTilt: 0.7,
      },
      plates: { side: 0.31, edge: 0.2, edgeFar: 1.2, ahead: 0.62, nearRear: 0.25, farRear: 0.3, max: 12, spacing: 0.32, y: 0.009, plate: [0.24, 0.016, 0.13], seamY: 0.021, seam: [0.125, 0.006, 0.038] },
    },
  },
  /** Unit proxies (`dungeon-world.js` `update`): body, head, a nose showing the facing, a blob shadow. */
  unit: {
    bossScale: 1.35,
    body: { width: 0.34, height: 0.55, depth: 0.29, center: 0.42 },
    smallBody: { width: 0.27, height: 0.32, depth: 0.29, center: 0.23 },
    head: { width: 0.29, height: 0.24, depth: 0.28, center: 0.78 },
    smallHead: { width: 0.25, height: 0.18, depth: 0.28, center: 0.48 },
    nose: { width: 0.12, height: 0.085, depth: 0.13, forward: 0.19, drop: 0.01 },
    shadow: { radiusX: 0.31, radiusZ: 0.22, y: 0.014, opacity: 0.42, segments: 12 },
    /** A fallen unit lies on its side, pivoting at its feet and lifted by half its thickness. */
    lieLift: 0.17,
    /** Instances the unit batches start with; they double when a floor holds more. */
    initialCapacity: 16,
  },
  /**
   * Sprite units (integration plan slice 3: `sprites.ts`, `sprite-anim.ts`,
   * `sprite-layer.ts`). Presentation, not balance.
   * - `tileMetres`: one dungeon tile in metres, the one scale that turns the
   *   contract's 29 px per metre into world size. Derived from the only known
   *   scale, the dungeon-floors prototype's (`dungeon-main.js`: Brad's 48×64
   *   cell drawn as a 0.71×0.95 plane per tile): a 64 px cell 0.95 tile tall at
   *   29 px/m is 64 / (29 × 0.95) ≈ 2.323 m per tile. Plane heights that follow:
   *   Brad and an M cell (64 px) 0.95 tile, an S cell (48 px) 0.71, an L cell
   *   (96 px) 1.43, an XL cell (128 px) 1.90. A sprite is never scaled to fix
   *   its size (contract §1): its cell and this constant decide it.
   * - `atlasMax`: the largest side of a repacked atlas in texels (the exports
   *   are one row of frames, 13,536 px wide for the golem, past any phone's
   *   texture limit; iOS Safari and many GPUs stop at 4096).
   * - `idleFps`: frames a second at Off while a sprite's loop plays on screen
   *   (the loops' own frames last 180 ms and more, so 12 is enough); none while
   *   the page is hidden or no loop is on screen.
   * - `missingFaintFade`: how far a fainted unit whose sheet has no `faint`
   *   lerps toward the background (its idle frame 0, held and dimmed; never
   *   rotated).
   * - `shadowFadeCut`: a sprite faded (an escape) past this stops casting its
   *   Medium shadow.
   * - `blobWidth`: the Off blob disc's x radius under a sprite, as a share of
   *   its cell's width in tiles (the proxy's disc, 0.31 under a 0.34-wide body,
   *   is about this share of what it stands on).
   * - `bossTint`: how far the boss's sprite is multiplied toward `palette.boss`.
   * - `walkResumeMs`: a move that starts within this long after the unit's
   *   last one ended continues the walk cycle's phase; later, it starts over.
   * - `alphaTest`: the hard alpha cut (the contract's alpha is 0 or 255).
   * - `initialCapacity`: instances per sheet's mesh at first; it doubles when a floor holds more.
   */
  sprite: {
    tileMetres: 64 / (29 * 0.95),
    atlasMax: 2048,
    idleFps: 12,
    missingFaintFade: 0.55,
    shadowFadeCut: 0.5,
    blobWidth: 0.4,
    bossTint: 0.6,
    walkResumeMs: 300,
    alphaTest: 0.5,
    initialCapacity: 4,
  },
  /** Frame intervals the view keeps for its stats (the stats panel's p90 window). */
  stats: { frames: 120 },
  /** The Medium tier (integration plan slice 2a). */
  medium: {
    /** Device pixel ratio cap (Rendering page: 1.5 or lower on phones). */
    pixelRatio: 1.5,
    /** Frames a second while idle (grain, dust motes and flames animate); none while the page is hidden. */
    idleFps: 30,
    /** Output tone-map exposure, ACES filmic (`dungeon-main.js`). */
    exposure: 1.1,
    shadowMapSize: 1024,
    /** The key light: offset from the floor centre, shadow camera range and bias, margin around the floor (`dungeon-main.js`). */
    sun: { offset: [-12, 28, 10], near: 0.5, far: 100, bias: -0.0005, normalBias: 0.03, margin: 3 },
    /** The lens (`dungeon-focus.js`, `dungeon-main.js`): the tilt radius is `blurShortEdge` of the frame's short edge. */
    lens: { strength: 1, taps: 8, blurShortEdge: 0.0135, focusHeight: 0.45, bandMin: 0.2, bandMax: 0.8, tiltRampTop: [0.2, 0.42], shadowChroma: 0.006, hazeRange: [50, 125], zoneGround: 2.4, acesSteps: 60 },
    /** Zone map texels per tile and its largest side (`dungeon-zones.js`). */
    zoneMap: { texelsPerTile: 2, maxSize: 512 },
    /** Strata height fields (`engine/src/geology.js`). */
    geology: {
      bed: 1.45,
      swell: [0.5, 5.3],
      ripple: [0.18, 2.1, 1],
      segmentAxis: [0.71, 0.69],
      segmentLength: 3.3,
      segmentWobble: [0.6, 0.37],
      warp: [
        [0.38, 0.21, 0.17],
        [0.16, 0.53, -0.37],
      ],
      jog: [
        [0.3, 2.7],
        [0.17, 7.3],
      ],
    },
    /** The tone atlas (`dungeon-pixel-atlas.js`): size, channel seeds, feature counts at full density. */
    atlas: { size: 256, seeds: [0x51a7, 0x2b0c, 0x7713, 0x0ca9], pebbles: 1500, groundCracks: 40, lenses: [18, 30], pits: 260, knots: 26, checks: 40, capCracks: 30 },
    /** Surface programs (`dungeon-surfaces.js`); `noTop` marks geometry without a face top edge. */
    surfaces: { minReference: 0.002, seedPeriod: 997, speckKeep: 0.25, groundWarmKeep: 0.45, noTop: 99 },
    /** The rock mass (`dungeon-rock-mass.js`, terraces from `dungeon-world.js`). */
    rock: { stubHeight: 0.24, wallHeight: 1.55, terraceStep: 0.6, terraceColumns: [-2, -1, 0, 1, 2], lowStub: 0.4, maxTrianglesPerCell: 28, seedPeriod: 997, capBend: 0.85 },
    /** Border rock beyond the floor (`dungeon-world.js`): [height, _, depth] of the rear and the apron, [height, width] of the sides. */
    border: { rear: [2.75, 0, 8], apron: [0.24, 0, 6], side: [2.15, 7.5], margin: 15 },
    /** The light bake (`dungeon-radiance.js`). */
    bake: {
      texelsPerTile: 4,
      minTexelsPerTile: 2,
      maxTexels: 256,
      slices: 8,
      floorY: -0.5,
      height: 4,
      maxRaySteps: 256,
      stageSharpness: 2.5,
      stageReach: 1.6,
      rockSpill: 0.8,
      occlusionReach: 0.6,
      occlusionPower: 0.8,
      rockDepth: [0.2, 2.4],
      occlusionFade: [0.3, 0.9],
      highRock: [1.2, 1.4],
      minVertical: 0.002,
      floorSlice: 0.5,
      rockShade: 0.6,
    },
    /** Fixture placement and the bake's fills (`dungeon-lighting.js`, `dungeon-markers.js`). */
    fixtures: {
      site: [0.4, 0.36],
      sconce: 0.4,
      height: 0.83,
      railLane: [0.3, 0.75],
      clear: 0.24,
      sconceClear: 0.3,
      sconceBand: [0.45, 1.1],
      sconceMinWall: 1.2,
      markerClear: 1.8,
      prioritySpacing: 1.15,
      relaxedSpacing: 1.8,
      sconceToFloor: 1.5,
      backWeight: 0.7,
      bossMix: 0.48,
      far: 999,
      obstacleMinTop: 0.12,
      roomView: { margin: 0.6, back: 0.4, northShare: 0.45, minHalf: 1.6, inset: 0.9 },
      lantern: { minRoomWidth: 3, roomInset: 0.66, portalAlong: 0.3, face: 0.19, side: 0.3, spacing: 2.4, drop: 0.28, hang: 0.04 },
      shaftInset: 0.6,
      shaftSpan: 2.7,
      shaftBack: 0.4,
      shaftDirection: [-0.6, 0.74, -0.22],
      zones: { shadeFeather: 1, torchRadius: 1, lanternRadius: 0.7, warmFeather: 1.6 },
      markers: {
        exit: { dx: 0.44, y: 0.73, dz: -0.24, radius: 1.4, gain: 1.05 },
        spring: { y: 0.16, radius: 1.05, gain: 0.55 },
        boss: { inset: 0.24, y: 0.65, radius: 2.1, gain: 0.8 },
        dais: { y: 0.18, radius: 1.25, gain: 0.45 },
      },
      bake: {
        low: 0.25,
        fillVertical: 0.95,
        stageLength: [1.4, 3.2, 0.38],
        stageWidth: [0.85, 1.45, 0.2],
        emptyStage: 0.65,
        approachSpacing: 2,
        encounterToward: 0.65,
        encounterRadius: 2.3,
        encounterGain: 1.3,
        encounterWidth: 1.8,
        routeWidth: 0.78,
        routeRadius: 1.7,
        poolVertical: 1.1,
        poolAbove: 0.9,
        poolMin: 1.4,
      },
    },
    /** Fixture shapes (`dungeon-lighting.js`): an iron A-frame brazier of nine boxes, a sconce, a lantern of three, a shaft crack. */
    brazier: {
      parts: 9,
      lanternParts: 3,
      leg: { angle: 0.5, spread: 0.19, foot: 0.56, top: 0.27, inward: 0.88, width: 0.034, extra: 0.02 },
      post: { y: 0.46, width: 0.042, height: 0.56 },
      band: { y: 0.36, width: 0.066, height: 0.035 },
      basket: { y: 0.74, width: 0.14, height: 0.05 },
      prong: { angle: 1.55, radius: 0.075, y: 0.79, width: 0.022, height: 0.1 },
      sconce: { back: 0.075, y: 0.66, thin: 0.03, wide: 0.12, height: 0.26, arm: 0.035, armLength: 0.09, armThin: 0.035 },
      flame: { y: 0.765, outer: [0.085, 0.25, 0.075], inner: [0.042, 0.15, 0.038], innerForward: 0.04 },
      lantern: {
        capLift: 0.1,
        hanger: 0.014,
        minHanger: 0.02,
        cap: [0.13, 0.035],
        baseDrop: 0.075,
        base: [0.11, 0.025],
        flameDrop: 0.06,
        outer: [0.055, 0.13, 0.05],
        inner: [0.03, 0.09, 0.028],
        innerForward: 0.02,
        hangerWarm: 0.1,
        bodyWarm: 0.35,
        hangerShade: 1.1,
        bodyShade: 1.6,
      },
      crack: { lift: 0.004, back: 0.04, size: [0.42, 0.02, 0.15] },
      /** How dark each part is: the fixture colour times these, warmed toward the flame. */
      shade: { legs: 0.22, band: 0.5, basket: 0.2, basketWarm: 0.2, prong: 0.32, prongWarm: 0.45 },
      glowY: 0.9,
      torchGlowCore: 0.3,
      exitGlowForward: 0.08,
      exitGlowScale: 0.72,
      lanternGlowLift: 0.02,
      lanternGlowCore: 0.4,
      lanternGlowScale: 0.55,
      poolY: 0.034,
      flameSides: 6,
      /** Flame tongue rings: [height, radius, lean]. */
      flameRings: [
        [0, 0.45, 0],
        [0.22, 1, -0.08],
        [0.52, 0.72, 0.05],
        [0.78, 0.34, 0.28],
        [1, 0.025, 0.48],
      ],
    },
    /** Coronas (`dungeon-glow.js`): pull toward the camera, draw order. */
    glow: { pull: 0.25, renderOrder: 3 },
    /** Dust decals: seed salt, tint scale, draw order, height, jitter, size [base, extra]. */
    decals: { salt: 0x5d1, tint: 0.025, renderOrder: 1, y: 0.028, jitter: 0.3, size: [0.3, 0.6] },
    /** Shafts (`engine/src/atmosphere.js`): texture size and seeds, the fixed sun and view directions, the soft contact fade. */
    atmosphere: {
      texture: [64, 256],
      textureSeed: 40117,
      motes: 140,
      seed: 5521,
      toSun: [-0.6, 0.74, -0.22],
      view: [7.8, 17.8, 34],
      start: 0.3,
      fade: [0.08, 0.75],
      renderOrder: 3,
      hiddenY: -50,
      /** The town's (slice 6a, `atmosphere.js` defaults): the key light the canopy dapples follow, the shafts' colour and soft fade, decal lift and recipes [add r, g, b, multiplier]. */
      keyToSun: [-19, 19, 8],
      townColor: [1, 0.56, 0.18],
      townFade: [0.5, 3.5],
      decalLift: 0.045,
      sunPool: [0.018, 0.009, 0.002, 1.9],
      dapple: [0.018, 0.01, 0.002, 1.9],
      lampPool: [0.34, 0.13, 0.012, 1.5],
    },
    /** Units' cut-out shadows (`engine/src/character-shadows.js`). */
    characterShadow: { opacity: 0.3, lift: 0.012, hideFall: 0.5 },
    /** The leader's cutaway (`engine/src/architecture-cutaway.js`): half height, ellipse shape, body-centre height. */
    cutaway: { reach: 0.8, width: 0.7, height: 0.9, anchor: 0.48 },
  },
} as const;
